import type { ReactNode } from 'react';
import './SegmentedControl.css';

export interface SegmentedControlProps<V extends string> {
  value: V;
  options: ReadonlyArray<{ value: V; label: ReactNode }>;
  onChange: (value: V) => void;
  /** Accessible name of the group, e.g. "Time range" */
  label: string;
  className?: string;
}

/**
 * Row of toggle buttons for picking one of a few options (e.g. a time range).
 * The selected option is marked with aria-pressed.
 *
 * @example
 * ```tsx
 * <SegmentedControl label="Time range" value={range} options={STATS_RANGES} onChange={setRange} />
 * ```
 */
export function SegmentedControl<V extends string>({
  value,
  options,
  onChange,
  label,
  className = '',
}: SegmentedControlProps<V>) {
  return (
    <div role="group" aria-label={label} className={['segmented', className].filter(Boolean).join(' ')}>
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className={`segmented-option ${isSelected ? 'segmented-option-selected' : ''}`.trim()}
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
